import { createReducer, WritableDraft } from "@reduxjs/toolkit";
import {
  Module,
  ModulesByParentId,
  ModuleType,
  Page,
} from "../../types/index.js";
import { moveArrayElement, removeFromArray } from "../../utils/utils.js";
import { getPages } from "../pages.js";
import { deleteModuleTranslationStart, dragSubmodule } from "./actions.js";
import { getModules, getSiteModules, postModule } from "./thunks.js";

export const initialState: ModulesByParentId = {};

const postModuleStartInternal = (
  state: WritableDraft<ModulesByParentId>,
  moduleId: string,
  moduleType: ModuleType,
  parentId: string | null,
  pageId: string | null,
) =>
  updateModules(state, parentId, pageId, moduleType, (currentModules) => [
    ...currentModules,
    moduleId,
  ]);

const updateModules = (
  state: ModulesByParentId,
  parentId: string | null,
  pageId: string | null,
  moduleType: ModuleType,
  callback: (subModuleIds: string[]) => string[],
): ModulesByParentId => {
  if (!parentId) return state;

  const byPage = state[String(pageId)] || {};
  const subModuleCategories = byPage[parentId] || {};
  const subModuleIds = subModuleCategories[moduleType] || [];
  const newSubmoduleIds = callback(subModuleIds);

  return {
    ...state,
    [String(pageId)]: {
      ...byPage,
      [parentId]: { ...subModuleCategories, [moduleType]: newSubmoduleIds },
    },
  };
};

const deleteModuleTranslationInternal = ({
  state,
  pageId,
  moduleId,
  moduleType,
  parentId,
  deleteAllTranslations,
}: {
  state: WritableDraft<ModulesByParentId>;
  pageId: string | null;
  moduleId: string;
  moduleType: ModuleType;
  parentId: string | null;
  deleteAllTranslations: boolean;
}) => {
  if (!deleteAllTranslations) return state;

  return updateModules(state, parentId, pageId, moduleType, (currentModules) =>
    removeFromArray<string>(currentModules, moduleId),
  );
};

const getModulesInternal = (
  state: ModulesByParentId,
  modules: Module[],
  pageId: string | null,
): ModulesByParentId => {
  const modulesByParentId = modules.reduce<ModulesByParentId>(
    (newState, { id, parentId, type }) => {
      return updateModules(
        newState,
        parentId,
        pageId,
        type,
        (currentModules) => [...currentModules, id],
      );
    },
    {},
  );

  return { ...state, ...modulesByParentId };
};

const getModulesFromPages = (state: ModulesByParentId, pages: Page[]) =>
  pages.reduce<ModulesByParentId>(
    (carryState, page) => getModulesInternal(carryState, page.modules, page.id),
    state,
  );

const dragSubmoduleInternal = ({
  state,
  pageId,
  moduleType,
  parentId,
  dragIndex,
  hoverIndex,
}: {
  state: WritableDraft<ModulesByParentId>;
  pageId: string | null;
  moduleType: ModuleType;
  parentId: string;
  dragIndex: number;
  hoverIndex: number;
}) =>
  updateModules(state, parentId, pageId, moduleType, (currentModules) =>
    moveArrayElement<string>(currentModules, dragIndex, hoverIndex),
  );

const reducer = createReducer(initialState, (builder) => {
  builder.addCase(postModule.pending, (state, action) => {
    const { pageId, moduleId, parentId, moduleType } = action.meta.arg;
    return postModuleStartInternal(
      state,
      moduleId,
      moduleType,
      parentId,
      pageId,
    );
  });

  builder.addCase(deleteModuleTranslationStart, (state, action) =>
    deleteModuleTranslationInternal({ state, ...action.payload }),
  );

  builder.addCase(getSiteModules.fulfilled, (state, action) =>
    getModulesInternal(state, action.payload, null),
  );

  builder.addCase(getModules.fulfilled, (state, action) =>
    getModulesInternal(state, action.payload, action.meta.arg.pageId),
  );

  builder.addCase(getPages.fulfilled, (state, action) =>
    getModulesFromPages(state, action.payload),
  );

  builder.addCase(dragSubmodule, (state, action) =>
    dragSubmoduleInternal({ state, ...action.payload }),
  );
});

export default reducer;
